// «Показатели» (вкладка «Телоскопа»): график любой метрики за период и сравнение двух метрик на двух осях.

import { useMemo, useState } from 'react'

import { TimeSeriesChart, type ChartSeries } from '@/components/charts/TimeSeriesChart'
import { Card, ErrorText, Select, Spinner } from '@/components/ui'
import { formatDate, formatNumber } from '@/lib/format'
import { todayIso, type Period } from '@/lib/periods'

import { useMetricTypes, useSeries, type MetricType, type Series } from './api'
import { dayPoints, metricOptions, seriesQuery } from './series'

const NONE = ''

function toChart(series: Series | undefined, type: MetricType | undefined, axis: 'left' | 'right'): ChartSeries | null {
  if (!series || !type) return null
  return {
    key: type.key,
    label: type.label_ru,
    unit: type.unit || undefined,
    axis,
    digits: 2,
    points: dayPoints(series.points),
  }
}

function LatestLine({ series }: { series: Series | undefined }) {
  if (!series?.latest) return <span className="text-muted">записей нет</span>
  const unit = series.metric.unit ? ` ${series.metric.unit}` : ''
  return (
    <span className="tabular-nums">
      последнее: <span className="font-semibold text-fg">{formatNumber(series.latest.value, { digits: 2 })}{unit}</span>{' '}
      <span className="text-muted">({formatDate(series.latest.day)})</span>
    </span>
  )
}

export function MetricsPage() {
  const types = useMetricTypes()
  const [primary, setPrimary] = useState('weight')
  const [secondary, setSecondary] = useState(NONE)
  const [period, setPeriod] = useState<Period>('3m')
  const today = todayIso()
  const query = seriesQuery(period, today)

  const first = useSeries(primary, query)
  const second = useSeries(secondary || null, query)

  const byKey = useMemo(() => new Map((types.data ?? []).map((t) => [t.key, t])), [types.data])
  const options = useMemo(() => metricOptions(types.data ?? []), [types.data])

  const chartSeries = [
    toChart(first.data, byKey.get(primary), 'left'),
    secondary ? toChart(second.data, byKey.get(secondary), 'right') : null,
  ].filter((s): s is ChartSeries => s !== null)

  const weekly = first.data?.bucket === 'week' || second.data?.bucket === 'week'
  const loading = first.isLoading || (Boolean(secondary) && second.isLoading)

  return (
    <>
      <Card>
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <Select
            label="Метрика"
            value={primary}
            options={options}
            onChange={(e) => setPrimary(e.target.value)}
          />
          <Select
            label="Сравнить с"
            value={secondary}
            placeholder="— без сравнения —"
            options={options.filter((o) => o.value !== primary)}
            onChange={(e) => setSecondary(e.target.value)}
          />
        </div>
        <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted">
          <span>
            {byKey.get(primary)?.label_ru ?? primary}: <LatestLine series={first.data} />
          </span>
          {secondary ? (
            <span>
              {byKey.get(secondary)?.label_ru ?? secondary}: <LatestLine series={second.data} />
            </span>
          ) : null}
        </div>
        <ErrorText error={types.error ?? first.error ?? second.error} className="mb-3" />
        {loading ? (
          <div className="flex h-[280px] items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <TimeSeriesChart
            series={chartSeries}
            period={period}
            onPeriodChange={setPeriod}
            height={280}
            today={today}
            emptyText="За этот период записей нет"
          />
        )}
        {weekly ? (
          <p className="mt-2 text-xs text-muted">Период больше года — точки показывают среднее за неделю.</p>
        ) : null}
      </Card>
    </>
  )
}
